import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { HomePlant } from '../../Services/typescript-fetch-client';
import decimalRound from '../../Utils/decimalRound';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleMinus, faCirclePlus, faFireFlameSimple, faLeaf, faWind } from '@fortawesome/free-solid-svg-icons';
import { Row } from 'primereact/row';
import { ColumnGroup } from 'primereact/columngroup';
import formatNumber from '../../Utils/formatNumber';
import { ReactComponent as WindSvg } from '../ui-components/icons/Eolico.svg';
import { ReactComponent as BioSvg } from '../ui-components/icons/Bioenergie.svg';
import { ReactComponent as GasSvg } from '../ui-components/icons/Gas.svg';
import { ReactComponent as SolarSvg } from '../ui-components/icons/Fotovoltaico_active.svg';
import TypeEnum = HomePlant.TypeEnum;
import ProfiliCaricoContext from '../../Context/ProfiliCaricoContext';

type Props = {
	type: HomePlant.TypeEnum;
	homePlants: HomePlant[];
};

const HomeAccordionTableComponent = (props: Props) => {
	let [isOpen, setIsOpen] = useState<boolean>(false);
	let context = useContext(ProfiliCaricoContext);

	function makeHeader() {
		return (
			<div
				className='flex justify-content-between align-items-center'
				onClick={() => {
					setIsOpen(!isOpen);
				}}>
				<h5>
					{mapTechnologyIcon(props.type)}{' '}
					<Link
						to={props.type}
						onClick={() => {
							context.setSidebarIsOpen!(false);
						}}
						className='plantLink'>
						{mapTechnologyName(props.type)}
					</Link>
				</h5>
				<div
					className='collapsor'
					onClick={() => {
						setIsOpen(!isOpen);
					}}>
					<FontAwesomeIcon icon={isOpen ? faCircleMinus : faCirclePlus} />
				</div>
			</div>
		);
	}

	function makeFooterTotals() {
		return (
			<ColumnGroup>
				<Row>
					<Column footer='TOTALE' align='left' footerClassName='home-accordion-col-footer' />
					<Column
						footer={formatNumber(
							decimalRound(
								props.homePlants.reduce((acc, hp) => {
									return acc + hp.eeToday!;
								}, 0),
								0
							)
						)}
						footerClassName='home-accordion-col-footer'
						align='right'
					/>
					<Column
						footer={formatNumber(
							decimalRound(
								props.homePlants.reduce((acc, hp) => {
									return acc + hp.prodExpectedToday!;
								}, 0),
								0
							)
						)}
						footerClassName='home-accordion-col-footer'
						align='right'
					/>
					{props.type === TypeEnum.Ccgt && isOpen ? <Column footer='' footerClassName='home-accordion-col-footer' /> : null}
					{props.type === TypeEnum.Ccgt && isOpen ? <Column footer='' footerClassName='home-accordion-col-footer' /> : null}
					<Column
						footer={
							props.homePlants.reduce((acc, hp) => {
								return acc + Number(hp.onProd!);
							}, 0) +
							'/' +
							props.homePlants.length
						}
						footerClassName='home-accordion-col-footer'
						align='right'
					/>
				</Row>
			</ColumnGroup>
		);
	}

	function mapTechnologyName(name: HomePlant.TypeEnum) {
		switch (name) {
			case HomePlant.TypeEnum.Ccgt:
				return 'CCGT';
			case HomePlant.TypeEnum.Bioenergy:
				return 'Bioenergie';
			case HomePlant.TypeEnum.Wind:
				return 'Eolico';
			case HomePlant.TypeEnum.Hydro:
				return 'Idroelettrico';
			case HomePlant.TypeEnum.Solar:
				return 'Fotovoltaico';
		}
	}

	function mapTechnologyIcon(name: HomePlant.TypeEnum) {
		switch (name) {
			default:
			case HomePlant.TypeEnum.Ccgt:
				return <GasSvg />;
			case HomePlant.TypeEnum.Bioenergy:
				return <BioSvg />;
			case HomePlant.TypeEnum.Wind:
				return <WindSvg />;
			case HomePlant.TypeEnum.Solar:
				return <SolarSvg className='fotovoltaico-icon' />;
		}
	}

	const plantLinkTemplate = (rowData) => {
		return (
			<Link to={`/${rowData.type}/${rowData.plantName}`} onClick={() => context.setSidebarIsOpen!(false)} className='plantLink'>
				{rowData.type === HomePlant.TypeEnum.Wind ? `${rowData.plantName} (${rowData.province.name})` : `${rowData.plantName}`}
			</Link>
		);
	};

	return (
		<div className='card'>
			<DataTable
				value={isOpen ? props.homePlants : []}
				responsiveLayout='scroll'
				size='small'
				className='dataTable'
				header={makeHeader}
				footerColumnGroup={makeFooterTotals()}>
				<Column field='plantName' header='Impianto' body={plantLinkTemplate} headerClassName='home-accordion-col-header' align='left' />
				<Column
					field='eeToday'
					header='EE prodotta oggi [MWh]'
					headerClassName='home-accordion-col-header'
					align='right'
					body={(v) => formatNumber(decimalRound(v.eeToday, 0))}
				/>
				<Column
					field='prodExpectedToday'
					header='Produzione attesa per oggi [MWh]'
					headerClassName='home-accordion-col-header'
					align='right'
					body={(v) => formatNumber(decimalRound(v.prodExpectedToday, 0))}
				/>
				{props.type === TypeEnum.Ccgt && isOpen ? (
					<Column
						field='powerTg1'
						header='TG1 [MW]'
						headerClassName='home-accordion-col-header'
						align='right'
						body={(v) => formatNumber(decimalRound(v.powerTg1, 0))}
					/>
				) : null}
				{props.type === TypeEnum.Ccgt && isOpen ? (
					<Column
						field='powerTg2'
						header='TG2 [MW]'
						headerClassName='home-accordion-col-header'
						align='right'
						body={(v) => formatNumber(decimalRound(v.powerTg2, 0))}
					/>
				) : null}
				<Column field='onProd' header='In marcia' headerClassName='home-accordion-col-header' align='right' body={booleanBody} />
			</DataTable>
		</div>
	);
};

const booleanBody = (rowData: any, item: any) => {
	if (typeof rowData[item.field] === 'boolean') return rowData[item.field] ? <span className='activePlant'>Sì</span> : <span>No</span>;
	else return rowData[item.field];
};

export default HomeAccordionTableComponent;
